import { API_BASE_URL } from "../../../config";
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerLink from '../../Components/CustomerLink';
import SaleCreditCell, { useReportCredit } from './SaleCreditControl';
import ImdMoneySection from './SaleReportDetail/ImdMoneySection';

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// Same classification convention as PaymentCodeReferenceSeeder (saledata-module) and
// DriverDailyReport.js's buildSummary (vector 2026-07-21: paymentCode was showing raw, e.g. "BT"/
// "CASH-500", instead of a readable name — reusing DriverDailyReport.js's exact logic here instead
// of introducing a separate reference-table join). Legacy codes (M-1/M-20/M-50/DC) predate this
// convention and fall through to the raw code, same as DriverDailyReport.js's own aggregation.
const CASH_PREFIX = 'CASH-';
const BANK_TRANSFER_CODE = 'BT';
const FUEL_CODE = 'FUEL';
const COIN_MAX_VALUE = 10;

const paymentCodeLabel = (code) => {
    if (!code) return '-';
    if (code === FUEL_CODE) return 'ค่าน้ำมัน';
    if (code === BANK_TRANSFER_CODE) return 'ยอดโอน';
    if (code.startsWith(CASH_PREFIX)) {
        const value = Number(code.slice(CASH_PREFIX.length)) || 0;
        if (value > 0 && value <= COIN_MAX_VALUE) return 'เหรียญ';
        if (value > 0) return `${value.toLocaleString()} บาท`;
    }
    return code;
};

const getSection = (obj, keys, def) => {
    for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return def;
};

const getItemsData = (report) => {
    if (!report) return { items: [], totalQuantity: 0, totalPrice: 0 };
    const ib = report.itemsBreakdown || report.items || report.itemList;
    if (ib && Array.isArray(ib.items)) {
        return { items: ib.items, totalQuantity: ib.totalQuantity || 0, totalPrice: ib.totalPrice || 0 };
    }
    const arr = Array.isArray(ib) ? ib : [];
    const totalQuantity = arr.reduce((s, x) => s + (Number(x?.quantity) || 0), 0);
    const totalPrice = arr.reduce((s, x) => s + (Number(x?.total) || Number(x?.price) || 0), 0);
    return { items: arr, totalQuantity, totalPrice };
};

// Group items by productName + unitPrice and sum quantity/total
const groupItems = (items) => {
    const map = {};
    items.forEach(item => {
        const key = `${item.productName || item.iceCode || '?'}__${item.unitPrice || 0}`;
        if (!map[key]) {
            map[key] = { productName: item.productName || item.iceCode || '?', unitPrice: item.unitPrice || 0, quantity: 0, total: 0 };
        }
        map[key].quantity += Number(item.quantity) || 0;
        map[key].total += Number(item.total) || 0;
    });
    return Object.values(map);
};

const getBagSalesData = (report) => {
    const bsb = report?.bagSalesBreakdown;
    if (!bsb || !Array.isArray(bsb.items)) return { items: [], totalQuantity: 0, totalPrice: 0 };
    return { items: bsb.items, totalQuantity: bsb.totalQuantity || 0, totalPrice: bsb.totalPrice || 0 };
};

// Group bag-sale lines by bagName + unitPrice and sum quantity/total
const groupBagSales = (items) => {
    const map = {};
    items.forEach(item => {
        const key = `${item.bagName || item.bagCode || '?'}__${item.unitPrice || 0}`;
        if (!map[key]) {
            map[key] = { bagName: item.bagName || item.bagCode || '?', unitPrice: item.unitPrice || 0, quantity: 0, total: 0 };
        }
        map[key].quantity += Number(item.quantity) || 0;
        map[key].total += Number(item.total) || 0;
    });
    return Object.values(map);
};

const AgentSaleDataDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const reportCode = params?.id;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fullReport, setFullReport] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [unverifying, setUnverifying] = useState(false);
    const [endingShift, setEndingShift] = useState(false);
    const [agentDisplayName, setAgentDisplayName] = useState('');
    const [salesList, setSalesList] = useState([]);
    const [loadingSalesList, setLoadingSalesList] = useState(false);
    const [salesListExpanded, setSalesListExpanded] = useState(false);
    const [customerNameMap, setCustomerNameMap] = useState({});
    const [editSaleMsdUid, setEditSaleMsdUid] = useState(null);
    const [editSaleCode, setEditSaleCode] = useState('');

    const fetchSalesList = async (rc) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token || !rc) return;
        setLoadingSalesList(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(rc)}/sales-list`;
            const headers = { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { headers });
            if (res.ok) setSalesList(await res.json());
        } catch (e) {
            console.error('fetchSalesList error:', e);
        } finally {
            setLoadingSalesList(false);
        }
    };

    const handleRecodeSale = async (msdUid, newSaleCode) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;
        try {
            const url = `${API_BASE_URL}/saledata/api/module/sales/${encodeURIComponent(msdUid)}/recode`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify({ sale_code: newSaleCode }) });
            if (!res.ok) { const txt = await res.text(); alert('แก้ไขไม่สำเร็จ: ' + txt); return; }
            setEditSaleMsdUid(null);
            setEditSaleCode('');
            // Full reload so totals/promotions/refills sections reflect the edit too,
            // not just the raw sales-list (vector 2026-07-10).
            await Promise.all([fetchFullReport(), fetchSalesList(reportCode)]);
            alert('แก้ไขสำเร็จครับ');
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        }
    };

    const handleVerify = async () => {
        const userStr = localStorage.getItem('user');
        if (!userStr) return;
        const token = JSON.parse(userStr)?.token;
        if (!token || !reportCode) return;
        setVerifying(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify({ verified: true }) });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            setFullReport(prev => {
                if (!prev) return prev;
                const clone = { ...prev };
                ['master', 'masterReport', 'report', 'masterData'].forEach(k => {
                    if (clone[k]) clone[k] = { ...clone[k], verified: true };
                });
                return clone;
            });
        } catch (e) {
            alert('ไม่สามารถปิดรายงานได้: ' + (e.message || ''));
        } finally {
            setVerifying(false);
        }
    };

    const handleUnverify = async () => {
        if (!window.confirm('ยืนยันยกเลิกการตรวจ? รายงานนี้จะเปิดให้แก้ไขได้อีกครั้ง')) return;
        const userStr = localStorage.getItem('user');
        if (!userStr) return;
        const token = JSON.parse(userStr)?.token;
        if (!token || !reportCode) return;
        setUnverifying(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}/unverify`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'PUT', headers });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            setFullReport(prev => {
                if (!prev) return prev;
                const clone = { ...prev };
                ['master', 'masterReport', 'report', 'masterData'].forEach(k => {
                    if (clone[k]) clone[k] = { ...clone[k], verified: false };
                });
                return clone;
            });
            await fetchSalesList(reportCode);
        } catch (e) {
            alert('ไม่สามารถยกเลิกการตรวจได้: ' + (e.message || ''));
        } finally {
            setUnverifying(false);
        }
    };

    const handleEndShift = async () => {
        if (!window.confirm('ยืนยันการจบกะ? คุณแน่ใจหรือไม่ว่าต้องการจบกะรายงานนี้ในขณะนี้')) return;
        const userStr = localStorage.getItem('user');
        if (!userStr) return;
        const token = JSON.parse(userStr)?.token;
        if (!token || !reportCode) return;
        setEndingShift(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/end-shift?reportCode=${encodeURIComponent(reportCode)}`;
            const headers = { 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'POST', headers });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            await fetchFullReport();
        } catch (e) {
            alert('ไม่สามารถจบกะได้: ' + (e.message || ''));
        } finally {
            setEndingShift(false);
        }
    };

    const fetchFullReport = async () => {
        const userStr = localStorage.getItem('user');
        if (!userStr) { navigate('/login'); return; }
        const token = JSON.parse(userStr)?.token;
        if (!token) { navigate('/login'); return; }
        if (!reportCode) { setError('ไม่พบรหัสรายงาน'); setLoading(false); return; }

        setLoading(true);
        setError('');
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}/full`;
            const headers = { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'GET', headers });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            const data = await res.json();
            setFullReport(data);
        } catch (e) {
            setError('เกิดข้อผิดพลาด: ' + (e.message || ''));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFullReport();
        fetchSalesList(reportCode);
    }, [reportCode, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve the regular customer registered by the factory (paired via AGC-TOKYO direct sale)
    // so the sales-list table can show a name instead of a raw customerId code.
    useEffect(() => {
        const fetchRegularCustomerNames = async () => {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;
            const token = JSON.parse(userStr)?.token;
            if (!token) return;
            try {
                const resp = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ type: 'REGULAR' }),
                });
                if (!resp.ok) return;
                const data = await resp.json();
                const list = Array.isArray(data) ? data : (Array.isArray(data?.regular) ? data.regular : (Array.isArray(data?.data) ? data.data : []));
                const map = {};
                list.forEach(c => {
                    const id = c?.customerUid || c?.uid || c?.id;
                    if (id) map[id] = c?.storeName || c?.customerName || '';
                });
                setCustomerNameMap(map);
            } catch (e) { /* silent */ }
        };
        fetchRegularCustomerNames();
    }, []);

    const master = useMemo(() => fullReport
        ? getSection(fullReport, ['master', 'masterReport', 'report', 'masterData'], null)
        : null, [fullReport]);

    const { items, totalQuantity, totalPrice } = useMemo(() => getItemsData(fullReport), [fullReport]);
    const grouped = useMemo(() => groupItems(items), [items]);
    const bagSalesData = useMemo(() => getBagSalesData(fullReport), [fullReport]);
    const groupedBagSales = useMemo(() => groupBagSales(bagSalesData.items), [bagSalesData]);

    const payments = useMemo(() =>
        asArray(getSection(fullReport, ['payments', 'paymentData', 'payment', 'paymentList'], [])),
        [fullReport]);
    const paymentTotal = useMemo(() => payments.reduce((s, x) => s + (Number(x?.total) || 0), 0), [payments]);

    const agentName = master?.sellerName || master?.sellerId || '';
    const isVerified = master?.verified === true;
    const credit = useReportCredit(reportCode);

    const isSuperAdmin = useMemo(() => {
        try {
            const permissions = JSON.parse(localStorage.getItem('user') || '{}')?.permissions;
            return Array.isArray(permissions) && permissions.includes('superadmin:*');
        } catch (e) {
            return false;
        }
    }, []);

    // A sale's customerId only carries useful info when it's a registered regular customer
    // paired to this agent's sale (e.g. AGC-TOKYO direct-factory sale) — when it's just the
    // agent's own id (the normal case), showing it again next to the report title is noise.
    const customerLabel = (sale) => {
        if (!sale?.customerId || sale.customerId === master?.sellerId) return '-';
        return <CustomerLink name={customerNameMap[sale.customerId]} code={sale.customerId} />;
    };

    useEffect(() => {
        if (!master?.sellerId) return;
        const fetchAgentName = async () => {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;
            const token = JSON.parse(userStr)?.token;
            if (!token) return;
            try {
                const resp = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ type: 'AGENT' }),
                });
                if (!resp.ok) return;
                const data = await resp.json();
                const list = Array.isArray(data) ? data : (Array.isArray(data?.agent) ? data.agent : (Array.isArray(data?.data) ? data.data : []));
                const matched = list.find(c => (c.agentCustomerUid || c.customerUid || c.uid) === master.sellerId);
                if (matched) {
                    const name = matched.agentName || matched.storeName || matched.name;
                    if (name) setAgentDisplayName(name);
                }
            } catch (e) { /* silent */ }
        };
        fetchAgentName();
    }, [master?.sellerId]);

    return (
        <div className="btc-container">
            <div className="btc-card" style={{ width: 'auto' }}>
                <h1>รายงานการขาย (ตัวแทน) {agentDisplayName || agentName}</h1>
                <div className="btc-content">
                    <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                        <button onClick={() => navigate('/SaleData-management')} className="btc-btn" style={{ backgroundColor: '#6c757d' }}>
                            กลับ
                        </button>
                        {isVerified
                            ? <button disabled className="btc-btn" style={{ backgroundColor: '#28a745' }}>✅ ตรวจสอบแล้ว</button>
                            : <button onClick={handleVerify} disabled={verifying} className="btc-btn" style={{ backgroundColor: '#28a745' }}>
                                {verifying ? 'กำลังบันทึก...' : 'ตรวจสอบแล้ว'}
                              </button>
                        }
                        {isVerified && isSuperAdmin && (
                            <button onClick={handleUnverify} disabled={unverifying} className="btc-btn" style={{ backgroundColor: '#dc3545' }}>
                                {unverifying ? 'กำลังยกเลิก...' : 'ยกเลิกการตรวจ'}
                            </button>
                        )}
                        {isSuperAdmin && (
                            <button onClick={handleEndShift} disabled={endingShift} className="btc-btn" style={{ backgroundColor: '#fd7e14' }}>
                                {endingShift ? 'กำลังจบกะ...' : 'จบกะ'}
                            </button>
                        )}
                    </div>

                    {loading && <p>กำลังโหลดข้อมูล...</p>}
                    {error && <p className="error">{error}</p>}

                    {!loading && !error && fullReport && master && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '20px' }}>

                            {/* Master summary */}
                            <div className="btc-section">
                                <h3>สรุปรายงาน</h3>
                                <div className="btc-summary-card">
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">ตัวแทน</span>
                                        <span className="btc-summary-value">{agentDisplayName || agentName}</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">รายได้รวม</span>
                                        <span className="btc-summary-value">{Number(master.totalPrice).toLocaleString()} ฿</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">จำนวนถุงรวม</span>
                                        <span className="btc-summary-value">{master.totalQty}</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">วันที่</span>
                                        <span className="btc-summary-value">{master.date}</span>
                                    </div>
                                    <div className="btc-summary-item">
                                        <span className="btc-summary-label">รหัสรายงาน</span>
                                        <span className="btc-summary-value" style={{ fontFamily: 'monospace', fontSize: '13px' }}>{master.reportCode}</span>
                                    </div>
                                    {isVerified && (
                                        <div className="btc-summary-item">
                                            <span className="btc-summary-label">สถานะ</span>
                                            <span className="btc-summary-value" style={{ color: '#dc3545', fontWeight: 'bold' }}>✅ ตรวจสอบแล้ว</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Spacer / empty column */}
                            <div />

                            {/* Items table */}
                            <div className="btc-section" style={{ gridColumn: 'span 2' }}>
                                <h3>ยอดขายรวม</h3>
                                {grouped.length === 0
                                    ? <p>ไม่มีข้อมูล</p>
                                    : (
                                        <table className="btc-table">
                                            <thead>
                                                <tr>
                                                    <th>ชื่อสินค้า</th>
                                                    <th>ราคาต่อหน่วย</th>
                                                    <th>จำนวนรวม</th>
                                                    <th>ราคารวม</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {grouped.map((it, i) => (
                                                    <tr key={i}>
                                                        <td>{it.productName}</td>
                                                        <td>{Number(it.unitPrice).toLocaleString()}</td>
                                                        <td>{it.quantity}</td>
                                                        <td>{Number(it.total).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมทั้งสิ้น:</td>
                                                    <td style={{ fontWeight: 'bold' }}>{totalQuantity}</td>
                                                    <td style={{ fontWeight: 'bold' }}>{Number(totalPrice).toLocaleString()}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )
                                }
                            </div>

                            {/* Bag sales table (ซื้อขายกระสอบ) */}
                            <div className="btc-section" style={{ gridColumn: 'span 2' }}>
                                <h3>ยอดขายกระสอบ</h3>
                                {groupedBagSales.length === 0
                                    ? <p>ไม่มีข้อมูล</p>
                                    : (
                                        <table className="btc-table">
                                            <thead>
                                                <tr>
                                                    <th>กระสอบ</th>
                                                    <th>ราคาต่อหน่วย</th>
                                                    <th>จำนวนรวม</th>
                                                    <th>ราคารวม</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupedBagSales.map((it, i) => (
                                                    <tr key={i}>
                                                        <td>{it.bagName}</td>
                                                        <td>{Number(it.unitPrice).toLocaleString()}</td>
                                                        <td>{it.quantity}</td>
                                                        <td>{Number(it.total).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมทั้งสิ้น:</td>
                                                    <td style={{ fontWeight: 'bold' }}>{bagSalesData.totalQuantity}</td>
                                                    <td style={{ fontWeight: 'bold' }}>{Number(bagSalesData.totalPrice).toLocaleString()}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )
                                }
                            </div>

                            {/* Sales list with recode */}
                            <div className="btc-section" style={{ gridColumn: 'span 2' }}>
                                <div
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', cursor: 'pointer' }}
                                    onClick={() => setSalesListExpanded(v => !v)}
                                >
                                    <h3 style={{ margin: 0 }}>รายการขาย ({salesList.length} รายการ)</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {isVerified && (
                                            <span style={{ background: '#dc3545', color: '#fff', borderRadius: '6px', padding: '3px 10px', fontSize: '13px' }}>
                                                ตรวจสอบแล้ว — ไม่สามารถแก้ไขได้
                                            </span>
                                        )}
                                        <span style={{ fontSize: '13px', color: '#495057' }}>{salesListExpanded ? '▲ ย่อ' : '▼ ขยาย (แก้ไข)'}</span>
                                    </div>
                                </div>
                                {salesListExpanded && loadingSalesList && <p>กำลังโหลดรายการขาย...</p>}
                                {salesListExpanded && !loadingSalesList && salesList.length > 0 && (
                                    <table className="btc-table" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>เวลา</th>
                                                <th>ลูกค้า</th>
                                                <th>saleCode</th>
                                                <th>จำนวน</th>
                                                <th>ยอด (฿)</th>
                                                <th>ค้าง</th>
                                                {!isVerified && <th>แก้ไข</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {salesList.map((sale) => (
                                                <tr key={sale.msdUid}>
                                                    <td>{sale.time ? sale.time.substring(0, 8) : ''}</td>
                                                    <td>{customerLabel(sale)}</td>
                                                    <td>
                                                        {editSaleMsdUid === sale.msdUid ? (
                                                            <input
                                                                type="text"
                                                                value={editSaleCode}
                                                                onChange={e => setEditSaleCode(e.target.value)}
                                                                placeholder="เช่น 1I40B3P-2I40B2P"
                                                                style={{ width: '180px', padding: '3px 6px', borderRadius: '4px', border: '1px solid #007bff' }}
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <code style={{ fontSize: '12px' }}>{sale.saleCode}</code>
                                                        )}
                                                    </td>
                                                    <td>{sale.quantity}</td>
                                                    <td>{Number(sale.total).toLocaleString()}</td>
                                                    <td><SaleCreditCell sale={sale} customerName={sale.customerId === master?.sellerId ? '' : customerNameMap[sale.customerId]} isVerified={isVerified} credit={credit} /></td>
                                                    {!isVerified && (
                                                        <td>
                                                            {editSaleMsdUid === sale.msdUid ? (
                                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                                    <button
                                                                        className="btc-btn btn-sm"
                                                                        style={{ backgroundColor: '#46af38', padding: '4px 8px' }}
                                                                        onClick={() => handleRecodeSale(sale.msdUid, editSaleCode)}
                                                                    >
                                                                        ✅
                                                                    </button>
                                                                    <button
                                                                        className="btc-btn btn-sm"
                                                                        style={{ backgroundColor: '#dc3545', padding: '4px 8px' }}
                                                                        onClick={() => { setEditSaleMsdUid(null); setEditSaleCode(''); }}
                                                                    >
                                                                        ✖
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    className="btc-btn btn-sm"
                                                                    style={{ backgroundColor: '#007bff', padding: '4px 8px' }}
                                                                    onClick={() => { setEditSaleMsdUid(sale.msdUid); setEditSaleCode(sale.saleCode || ''); }}
                                                                >
                                                                    ✏️
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                {salesListExpanded && !loadingSalesList && salesList.length === 0 && <p>ยังไม่มีรายการขายในรายงานนี้</p>}
                            </div>

                            {/* เงินที่ส่ง — บัญชีกรอกเอง (ใบเอเย่นต์ไม่มี IMD) แล้วไปโผล่ในตารางการชำระเงินด้านล่างและ accounting-daily */}
                            <div style={{ gridColumn: 'span 2' }}>
                                <ImdMoneySection reportCode={reportCode} mode="agent" onSaved={fetchFullReport} />
                            </div>

                            {/* Payments */}
                            <div className="btc-section" style={{ gridColumn: 'span 2' }}>
                                <h3>การชำระเงิน</h3>
                                {payments.length === 0
                                    ? <p>ไม่มีข้อมูล</p>
                                    : (
                                        <table className="btc-table">
                                            <thead>
                                                <tr>
                                                    <th>วิธีชำระเงิน</th>
                                                    <th>จำนวน</th>
                                                    <th>รวม</th>
                                                    <th>หมายเหตุ</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payments.map((p, i) => (
                                                    <tr key={i}>
                                                        <td>{paymentCodeLabel(p.paymentCode)}</td>
                                                        <td>{p.amount}</td>
                                                        <td>{Number(p.total).toLocaleString()}</td>
                                                        <td>{p.note || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>รวมรับชำระ:</td>
                                                    <td style={{ fontWeight: 'bold' }}>{Number(paymentTotal).toLocaleString()}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    )
                                }
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgentSaleDataDetail;
